import * as signalR from "@microsoft/signalr";
import { ChatMessagePayload } from "../api/types";
import { apiCall } from "../api/api";
import { CONFIG } from "../config";

let connection: signalR.HubConnection | null = null;
let connectingPromise: Promise<signalR.HubConnection> | null = null;

export const establishSignalRConnection = async (
  chatNumber: string,
  userId: string,
  requestToken: string,
  onMessageReceived: (message: string) => void,
  onChatSeen?: (payload: ChatMessagePayload) => void,
  onConnectionEvent?: (payload: ChatMessagePayload) => void,
  onUpdatedChat?: (payload: ChatMessagePayload) => void,
  onChatDeleted?: (payload: ChatMessagePayload) => void,
  onChatTransferred?: (payload: ChatMessagePayload) => void,
): Promise<signalR.HubConnection> => {
  const dynamicTarget = `ReceiveMessage_${chatNumber}_${userId}`;
  console.log("Subscribing to target:", dynamicTarget);

  const registerHandlers = (conn: signalR.HubConnection) => {
    conn.off(dynamicTarget);
    conn.on(dynamicTarget, (payload: ChatMessagePayload) => {
      console.log("📩 Incoming WebSocket:", payload);

      if (payload.type === "connection") {
        console.log("connectionnn");

        // 🔥 Pass full payload to connection handler
        onConnectionEvent?.(payload);
        return;
      }

      if (payload.type === "chatSeen") {
        onChatSeen?.(payload);
        return;
      }

      if (payload.type === "updatedChat") {
        onUpdatedChat?.(payload);
        return;
      }

      if (payload.type === "chatDeleted") {
        onChatDeleted?.(payload);
        return;
      }

      if (payload.type === "chatTransferred") {
        onChatTransferred?.(payload);
        return;
      }

      if (payload.type === "message") {
        // console.log("messagee")
        // 🔥 Keep backwards compatibility (message only)
        if (payload.from !== userId) {
          onMessageReceived(payload.text);
        }
      }
    });
  };

  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    console.log("✅ Already connected");
    registerHandlers(connection);
    return connection;
  }

  if (connectingPromise) {
    console.log("⏳ Already in progress, waiting...");
    const existingConnection = await connectingPromise;
    registerHandlers(existingConnection);
    return existingConnection;
  }

  console.log("🔌 Establishing new SignalR connection...");

  connectingPromise = (async () => {
    try {
      // The negotiate endpoint runs on a serverless plan and can take several seconds to
      // respond on a cold start, so this needs a real timeout (not the browser's unbounded
      // default) plus actual retries, instead of failing silently on a non-OK response.
      // JWT-checked route on the same "chat" hub, so ReceiveMessage_* targets still arrive.
      const { url, accessToken } = await apiCall<{
        url: string;
        accessToken: string;
      }>(`private-chat/realtime/${userId}/negotiate`, {
        method: "POST",
        baseUrl: CONFIG.apiUrlSecondary,
        token: requestToken,
        timeoutMs: 20000,
        retries: 2,
        delayMs: 1000,
      });

      const conn = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => accessToken,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();

      registerHandlers(conn);

      await conn.start();
      console.log("✅ SignalR connected");

      connection = conn;
      return conn;
    } finally {
      connectingPromise = null;
    }
  })();

  return connectingPromise;
};

export const clearSignalRConnection = (
  conn: signalR.HubConnection | null | undefined,
) => {
  if (connection === conn) {
    connection = null;
  }
};

export const closeSignalRConnection = async () => {
  if (connection) {
    try {
      await connection.stop();
      console.log("🔌 SignalR connection stopped manually");
    } catch (err) {
      console.error("Error stopping SignalR connection:", err);
    } finally {
      connection = null;
      connectingPromise = null;
    }
  }
};
